import api from './api';

//The minimum amount of pixels (x or y) that the cursor has to move before it will be tracked as an event.
const _minimumDeviation = 30;
//The amount of time in seconds before state will be set to idle.
const _maxIdleTimeInSeconds = 30;
//The minumum amount of time in seconds between each tracked event.
const _trackIntervalInSeconds = 1;
//The amount of time in seconds between each post of history to api endpoint.
const _postIntervalInSeconds = 30;
//A second in miliseconds.
const _secondInMs = 1000;

//Types of mouse events.
const eventType = {
    idle: 0,
    move: 1
};

//Tracking states.
const state = {
    idle: 0,
    active: 1
};

//Initial event.
let _lastTrackedEvent = {
    mouseX: 0,
    mouseY: 0,
    eventDate: new Date(),
    eventType: eventType.idle,
    seconds: 0
};

//current tracking state
let _currentState = state.idle;
 //current idle time
let _idleTime = 0;
//history of tracked events
let _history = [];

const initTracker = () => {
    
    //Get any history saved in local storage.
    _history = getHistory();

    //Get snowplowId
    _lastTrackedEvent.snowplowId = getSnowPlowId();

    //Event listener for mousemove to track mouse movement.
    document.addEventListener('mousemove', (event) => {
        track(event);
    });

    //Event listener for window unload. Saves currently unsent history to local storage.
    window.addEventListener('unload', saveHistory);

    //Start posting
    startPostInterval(); 

    //Start timing idle time
    startIdleInterval();
};

//Post history to endpoint every x seconds.
//Clear history whether successful or not to avoid flooding local storage.
const startPostInterval = () => {
    const intervalHandler = () => {
        if (_history.length !== 0) {
            var historyToPost = _history;
            clearHistory();
            // TODO: re-add historyToPost to _history on failure?
            api.postHistory(historyToPost).then(
                () => { },
                () => { }
            );
        }
    };
    // send saved history asap
    if (_history && _history.length !== 0) { setTimeout(intervalHandler, 500); }

    // register send interval
    setInterval(intervalHandler, _postIntervalInSeconds * _secondInMs);
};

//Increments idleTime every second
//If idle time reaches max idle time, adds an idle event to history.
const startIdleInterval = () => {
    setInterval(() => {
        _idleTime ++; //Increase idle time.

        //When we reach max threshhold and we're not already idle, set state to idle. 
        if(_currentState != state.idle && _idleTime >= _maxIdleTimeInSeconds) {
            _currentState = state.idle;
            addEvent(); //for now, no included event means idle event.
        }
     }, _secondInMs);
};

//Event handler for document.onmousemove
//Resets idle time to 0.
//Sets state to active.
//Tracks movement if conditions are met.
const track = (event) => {
    _idleTime = 0;
    _currentState = state.active;

    //If mouse moved enough and track interval timer has passed, add event.
    if(trackIntervalTimePassed() && axisMoved(event)) {
        addEvent(event);
    }
};

//Checks if x axis has moved more than the minimum pixels since last event.
const axisMoved = (event) => {
    const xUpperLimit = _lastTrackedEvent.mouseX + _minimumDeviation;
    const xLowerLimit = _lastTrackedEvent.mouseX - _minimumDeviation;
    const yUpperLimit = _lastTrackedEvent.mouseY + _minimumDeviation;
    const yLowerLimit = _lastTrackedEvent.mouseY - _minimumDeviation;

    return (event.pageX > (xUpperLimit) || event.pageX < (xLowerLimit)) ||
    (event.pageX > (yUpperLimit) || event.pageX < (yLowerLimit));
};

//Checks if the minumum interval between events has passed.
const trackIntervalTimePassed = () => {
    const toCheck = new Date(Date.now() - (_trackIntervalInSeconds * _secondInMs));
    return _lastTrackedEvent.eventDate < toCheck;
};

//Return the last tracked coordinates
const currentPosition = () => {
    return {
        x: _lastTrackedEvent.mouseX,
        y: _lastTrackedEvent.mouseY
    };
};

//Adds an event to history and updates lastTrackedEvent.
//For now, no mouse event means idle, mouse event means move.
const addEvent = (event) => {

    const date = new Date();
    const trackedEvent = {};

    if(event != null) {
        //Move event. Update position.
        //Round x and y based on minimum deviation.
        trackedEvent.mouseX = Math.floor(event.pageX / _minimumDeviation) * _minimumDeviation + (_minimumDeviation / 0.5);
        trackedEvent.mouseY = Math.floor(event.pageY / _minimumDeviation) * _minimumDeviation + (_minimumDeviation / 0.5);
        trackedEvent.eventType = eventType.move;
    } else {
        //Idle event. position is same as last tracked.
        trackedEvent.mouseX = _lastTrackedEvent.mouseX;
        trackedEvent.mouseY = _lastTrackedEvent.mouseY;
        trackedEvent.eventType = eventType.idle;
    }

    //Properties shared by all events.
    trackedEvent.eventDate = date;
    trackedEvent.seconds = getDifferenceInSeconds(_lastTrackedEvent.eventDate, date);
    trackedEvent.snowplowId = _lastTrackedEvent.snowplowId;

    addEventToPage(trackedEvent);
    _lastTrackedEvent = trackedEvent;
};

const addEventToPage = (trackedEvent) => {
    
    //Try to find page in existing history
    var page = _history.find((page) => {
        return page.url === window.location.href;
    });

    //If no page exists, create one.
    if(page == null) {
        
        //Add event to page events.
        const events = [];
        events.push(trackedEvent);
        
        _history.push({
            url: window.location.href,
            events: events
        });
    } else {
        //Add event to page events.
        page.events.push(trackedEvent);
    }
};

//Gets any stored history from local storage and clears localStorage.
const getHistory = () => {
    const stringHistory = localStorage.getItem('mouseHistory') || '[]';
    localStorage.setItem('mouseHistory', null);

    const history = JSON.parse(stringHistory);
    return history || [];
};

//Clears client side tracked history.
const clearHistory = () => {
    _history = [];
};

//Save history to local storage.
const saveHistory = () => {
    localStorage.setItem('mouseHistory', JSON.stringify(_history));
};

//Calculates the difference in seconds between two dates.
const getDifferenceInSeconds = (date1, date2) => {
    const difference = (date1.getTime() - date2.getTime()) / 1000;

    //return rounded down to integer and do not differentiate between negative and positive.
    return Math.floor(Math.abs(difference));
};

//Gets snowplow id from cookies
const getSnowPlowId = () => {
    const identifier = '_sp_id';

    //Get cookies string
    const decodedCookie = decodeURIComponent(document.cookie);
    
    //Split into separate cookies
    const cookies = decodedCookie.split(';');

    //Find right cookie
    for(let i = 0 ; i < cookies.length ; i++) {
        
        if(cookies[i].indexOf(identifier) != -1) {
            
            //Get value
            const splitCookie = cookies[i].split('=');
            
            //If no value, return null
            if(splitCookie.lenght < 2) {
                return null;
            }

            //Value is multiple id's split by full stops, we want the first one
            const splitValue = splitCookie[1].split('.');

            //If empty return null otherwise return value.
            return splitValue[0] != '' ? splitValue[0] : null;
        }
    }

    //if no cookies return null.
    return null;
  };

export default {
    initTracker,
    currentPosition
};